/**
 * Modal to fill out a consultation note for a consultation-stage episode (contextual Action).
 */

import { Button, Group, Modal, Select, Stack, Textarea } from '@mantine/core';
import { useState } from 'react';
import type { StageRow } from './StageTab';

export interface ConsultationNoteModalProps {
  open: boolean;
  onClose: () => void;
  row: StageRow | null;
  onSave?: (payload: { outcome: string; note: string }) => void;
}

const OUTCOME_OPTIONS = [
  { value: 'matched', label: 'Matched to provider' },
  { value: 'booked', label: 'First session booked' },
  { value: 'reschedule', label: 'Reschedule' },
  { value: 'inactive', label: 'Move to inactive' },
];

export function ConsultationNoteModal({
  open,
  onClose,
  row,
  onSave,
}: ConsultationNoteModalProps): JSX.Element {
  const [outcome, setOutcome] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const handleSave = (): void => {
    onSave?.({ outcome: outcome ?? '', note });
    onClose();
  };

  return (
    <Modal opened={open} onClose={onClose} title="Consultation note" size="md">
      <Stack gap="md">
        {row && (
          <Stack gap="xs">
            <span><strong>Client</strong> {(row as { clientName?: string }).clientName ?? '—'}</span>
            <span><strong>Service type</strong> {(row as { episodeServiceType?: string }).episodeServiceType ?? '—'}</span>
          </Stack>
        )}
        <Select
          label="Outcome"
          placeholder="Select outcome"
          data={OUTCOME_OPTIONS}
          value={outcome}
          onChange={setOutcome}
        />
        <Textarea label="Note" placeholder="Consultation note" value={note} onChange={(e) => setNote(e.currentTarget.value)} minRows={3} />
        <Group mt="md" justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>Save</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
